import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, MessageCircle, Bookmark, Share2, Image as ImageIcon, TrendingUp, Wine, CalendarDays } from "lucide-react";
import { StarRating } from "@/components/kit";
import { toast } from "sonner";

const TYPE_LABEL = { rating: "rated a bottle", bar: "built a bar", registry: "updated a registry", group: "group update", tasting: "shared a tasting note", event: "is planning an event" };

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [text, setText] = useState("");
  const [liked, setLiked] = useState({});

  const load = () => api.get("/feed").then((r) => setPosts(r.data));
  useEffect(() => { load(); api.get("/groups").then((r) => setGroups(r.data.filter((g) => g.trending))); }, []);

  const post = async () => {
    if (!text.trim()) return;
    await api.post("/feed", { text, type: "tasting" });
    setText(""); toast.success("Posted to your feed"); load();
  };
  const like = async (p) => {
    setLiked((s) => ({ ...s, [p.id]: !s[p.id] }));
    await api.post(`/feed/${p.id}/like`).catch(() => {});
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 lg:grid-cols-[1fr_320px] lg:px-6">
      <div className="space-y-5">
        {/* composer */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex gap-3">
            <img src={user?.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
            <textarea data-testid="post-composer-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
              placeholder={`What are you pouring, ${user?.name?.split(" ")[0]}?`}
              className="flex-1 resize-none rounded-lg border border-border bg-background/60 p-3 text-sm outline-none focus:border-primary" />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><ImageIcon size={16} /> Photo</button>
            <button data-testid="post-composer-submit" onClick={post} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Share</button>
          </div>
        </div>

        {posts.map((p) => (
          <article key={p.id} data-testid={`feed-post-${p.id}`} className="overflow-hidden rounded-xl border border-border bg-card">
            <div className="flex items-center gap-3 p-4">
              <img src={p.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
              <div><p className="text-sm font-semibold">{p.author} <span className="font-normal text-muted-foreground">· {TYPE_LABEL[p.type] || "posted"}</span></p><p className="text-xs text-muted-foreground">{p.time}</p></div>
            </div>
            <p className="px-4 pb-3 text-[15px]">{p.text}</p>
            {p.type === "rating" && p.meta?.score && <div className="px-4 pb-3"><StarRating value={p.meta.score} /></div>}
            {p.image && <img src={p.image} alt="" className="max-h-96 w-full object-cover" />}
            <div className="flex items-center gap-6 px-4 py-3 text-sm text-muted-foreground">
              <button data-testid={`like-${p.id}`} onClick={() => like(p)} className={`inline-flex items-center gap-1.5 hover:text-primary ${liked[p.id] ? "text-primary" : ""}`}><Heart size={16} className={liked[p.id] ? "fill-primary" : ""} /> {p.likes + (liked[p.id] ? 1 : 0)}</button>
              <button className="inline-flex items-center gap-1.5 hover:text-primary"><MessageCircle size={16} /> {p.comments}</button>
              <button onClick={() => toast.success("Saved")} className="inline-flex items-center gap-1.5 hover:text-primary"><Bookmark size={16} /> Save</button>
              <button onClick={() => toast.success("Share link copied")} className="ml-auto inline-flex items-center gap-1.5 hover:text-primary"><Share2 size={16} /> Share</button>
            </div>
          </article>
        ))}
      </div>

      {/* right rail */}
      <aside className="hidden space-y-5 lg:block">
        <RailCard icon={TrendingUp} title="Trending groups">
          {groups.map((g) => <Link key={g.id} to={`/groups/${g.id}`} className="flex items-center justify-between py-1.5 text-sm hover:text-primary"><span>{g.name}</span><span className="text-xs text-muted-foreground">{(g.members / 1000).toFixed(1)}k</span></Link>)}
        </RailCard>
        <RailCard icon={Wine} title="Top bottles this week">
          {["Buffalo Trace 4.7", "Veuve Clicquot 4.8", "Don Julio Añejo 4.6"].map((t) => <div key={t} className="py-1.5 text-sm">{t}</div>)}
        </RailCard>
        <RailCard icon={CalendarDays} title="Upcoming events">
          <div className="py-1.5 text-sm">Bourbon Tasting Night · Sat</div>
          <div className="py-1.5 text-sm">Champagne Brunch · Sun</div>
        </RailCard>
      </aside>
    </div>
  );
}

function RailCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center gap-2 font-semibold"><Icon size={16} className="text-primary" /> {title}</div>
      {children}
    </div>
  );
}
